import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Brain, CheckCircle2, Crown, IndianRupee, LineChart, Mic2, Palette, PlayCircle, UploadCloud } from 'lucide-react';
import SEO from '@/components/SEO';
import { useArtistAuth } from '@/context/ArtistAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const disciplines = [
  { key: 'Painting', icon: Palette, text: 'Composition, colour balance, originality, display readiness, and pricing direction.' },
  { key: 'Singing', icon: Mic2, text: 'Voice presentation, genre fit, audience appeal, recording tips, and practice drills.' },
  { key: 'Dance', icon: PlayCircle, text: 'Stage energy, expression, posture, rhythm, choreography clarity, and improvement drills.' },
  { key: 'Instrumental Music', icon: Mic2, text: 'Riyaz direction, tone, rhythm, hand control, repertoire, and stage-readiness feedback.' },
  { key: 'Craft & Handmade Art', icon: Palette, text: 'Material handling, finish, design refinement, product appeal, and display guidance.' },
  { key: 'Rangoli', icon: Palette, text: 'Symmetry, colour balance, devotional/event impact, floor composition, and finishing ideas.' },
  { key: 'Pottery / Ceramics', icon: Palette, text: 'Form, proportion, clay handling, surface finish, utility, and product direction.' },
  { key: 'Crochet / Textile Craft', icon: Palette, text: 'Pattern clarity, finishing, colour pairing, product durability, and pricing direction.' },
];

const mentorSubOptions = {
  Painting: ['Watercolor', 'Acrylic', 'Oil', 'Pencil sketch', 'Charcoal', 'Digital painting', 'Portrait', 'Landscape'],
  Singing: ['Classical', 'Semi-classical', 'Folk', 'Bhajan', 'Ghazal', 'Film / Playback', 'Indie'],
  Dance: ['Kathak', 'Bharatanatyam', 'Odissi', 'Kuchipudi', 'Folk Dance', 'Contemporary', 'Hip-hop', 'Choreography'],
  'Instrumental Music': ['Tabla', 'Sitar', 'Violin', 'Flute', 'Harmonium', 'Guitar', 'Piano / Keyboard', 'Sarangi', 'Sarod', 'Santoor', 'Mridangam', 'Dholak', 'Drums / Percussion', 'Other instrument'],
  'Craft & Handmade Art': ['Crochet work', 'Handmade craft', 'Clay craft', 'Wood craft', 'Textile craft', 'Jewellery craft', 'Paper craft', 'Other craft'],
  Rangoli: ['Traditional Rangoli', 'Festival Rangoli', 'Devotional Rangoli', 'Portrait Rangoli', 'Powder Rangoli', 'Flower Rangoli'],
  'Pottery / Ceramics': ['Wheel pottery', 'Hand-built pottery', 'Terracotta', 'Studio pottery', 'Decorative ceramics', 'Utility pottery'],
  'Crochet / Textile Craft': ['Crochet wearables', 'Crochet decor', 'Embroidery', 'Weaving', 'Textile craft', 'Macrame'],
};

const included = [
  'AI reviews images, audio, video, links, or written work notes',
  'Feedback changes by discipline: dance, singing, painting, and creative portfolio',
  'Practice drills and next-step improvement plan',
  'Market intelligence, pricing direction, and audience-fit suggestions',
  '12-month growth roadmap on paid plans',
  'Opportunity discovery for competitions, gigs, platforms, and showcases',
];

const FALLBACK_AI_MENTOR_PLANS = [
  {
    id: 'starter',
    name: 'Starter Mentor',
    monthly_price: 99,
    annual_price: 899,
    feedback_quota: 5,
    features: ['5 AI Mentor reviews/month', 'Discipline-specific feedback', 'Practice drills', 'Basic growth tips'],
  },
  {
    id: 'creator',
    name: 'Creator Mentor',
    monthly_price: 199,
    annual_price: 1791,
    feedback_quota: 10,
    highlight: true,
    features: ['10 AI Mentor reviews/month', 'Market direction', 'Pricing guidance', 'Monthly growth plan'],
  },
  {
    id: 'professional',
    name: 'Professional Mentor',
    monthly_price: 399,
    annual_price: 3591,
    feedback_quota: 20,
    features: ['20 AI Mentor reviews/month', 'Advanced critique', 'Opportunity suggestions', 'Portfolio improvement plan'],
  },
  {
    id: 'studio',
    name: 'Studio Mentor',
    monthly_price: 499,
    annual_price: 4491,
    feedback_quota: 50,
    features: ['50 AI Mentor reviews/month', 'For teams and frequent creators', 'Priority manual activation', 'Campaign guidance'],
  },
  {
    id: 'payg',
    name: 'Pay-as-you-go Review',
    monthly_price: 25,
    annual_price: null,
    feedback_quota: 1,
    features: ['1 premium AI Mentor review', 'No monthly plan required', 'Good for testing feedback quality'],
  },
];

const normalizePlans = (plans) => {
  const source = Array.isArray(plans) && plans.length ? plans : FALLBACK_AI_MENTOR_PLANS;
  return source.filter((plan) => plan?.id);
};

const friendlyError = (err, fallback) => {
  const detail = err.response?.data?.detail;
  if (detail === 'Not Found' || err.response?.status === 404) {
    return 'AI Mentor service is still refreshing on the backend. Please try again after deployment finishes, or ask admin to activate access.';
  }
  return detail || fallback;
};

const AiMentor = () => {
  const { artist, isAuthenticated } = useArtistAuth();
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('creator');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [feedbackForm, setFeedbackForm] = useState({
    discipline: 'Painting',
    sub_discipline: mentorSubOptions.Painting[0],
    media_type: 'image',
    title: '',
    media_url: '',
    context: '',
  });
  const [mentorUpload, setMentorUpload] = useState(null);
  const [uploadingMentorMedia, setUploadingMentorMedia] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [review, setReview] = useState('');

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const res = await axios.get(`${API}/ai-mentor/plans`);
        setPlans(normalizePlans(res.data?.plans));
      } catch (err) {
        setPlans(FALLBACK_AI_MENTOR_PLANS);
        setNotice('Showing launch AI Mentor plans while the live plan service refreshes.');
      }
    };
    loadPlans();
  }, []);

  useEffect(() => {
    const loadStatus = async () => {
      if (!artist?.id) return;
      try {
        const res = await axios.get(`${API}/ai-mentor/status/${artist.id}`);
        setStatus(res.data);
      } catch (err) {
        setStatus(null);
      }
    };
    loadStatus();
  }, [artist?.id]);

  const paidPlans = useMemo(() => plans.filter((plan) => plan.id !== 'free'), [plans]);
  const currentPlan = status?.subscription?.plan;

  const loginArtist = () => {
    window.dispatchEvent(new Event('openArtistJoin'));
  };

  const requestAccess = async (planId) => {
    setError('');
    setNotice('');
    if (!artist?.id) {
      loginArtist();
      setNotice('Please login as an artist before requesting AI Mentor Pro access.');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/ai-mentor/access-request`, {
        artist_id: artist.id,
        plan_id: planId,
        billing_cycle: planId === 'payg' ? 'payg' : billingCycle,
        note: 'Artist requested AI Mentor Pro access from the website.',
      });
      const statusRes = await axios.get(`${API}/ai-mentor/status/${artist.id}`);
      setStatus(statusRes.data);
      setNotice(response.data?.message || 'AI Mentor Pro access request sent. KalaManch admin will activate early access manually.');
    } catch (err) {
      setError(friendlyError(err, 'AI Mentor Pro access request could not be sent.'));
    } finally {
      setLoading(false);
    }
  };

  const submitFeedback = async () => {
    setError('');
    setNotice('');
    setReview('');
    if (!artist?.id) {
      loginArtist();
      setNotice('Please login as an artist to request AI Mentor feedback.');
      return;
    }
    if (!feedbackForm.context.trim() && !feedbackForm.media_url.trim() && !mentorUpload) {
      setError('Add an image, media URL/link, or describe what you want AI Mentor to review.');
      return;
    }
    setLoading(true);
    setUploadingMentorMedia(Boolean(mentorUpload));
    try {
      let mediaUrl = feedbackForm.media_url.trim();
      let mediaType = feedbackForm.media_type;
      if (mentorUpload) {
        const formData = new FormData();
        formData.append('file', mentorUpload);
        const uploadRes = await axios.post(`${API}/uploads/ai-mentor-media`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        mediaUrl = uploadRes.data?.image_url || mediaUrl;
        mediaType = 'image';
      }
      const res = await axios.post(`${API}/ai-mentor/feedback/generate`, {
        artist_id: artist.id,
        ...feedbackForm,
        media_type: mediaType,
        media_url: mediaUrl,
      });
      setReview(res.data?.review?.feedback || '');
      const statusRes = await axios.get(`${API}/ai-mentor/status/${artist.id}`);
      setStatus(statusRes.data);
      setNotice('AI Mentor feedback generated and saved to your paid mentor history.');
      setMentorUpload(null);
    } catch (err) {
      setError(friendlyError(err, 'AI Mentor feedback could not be generated.'));
    } finally {
      setLoading(false);
      setUploadingMentorMedia(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#F6F8FB] pt-28 sm:pt-32">
      <SEO
        title="KalaManch AI Mentor Pro | Paid AI Growth Coach for Artists"
        description="Request KalaManch AI Mentor Pro access for discipline-specific feedback, practice drills, market intelligence, growth plans, and artist opportunity discovery."
        keywords="KalaManch AI Mentor Pro, paid AI mentor for artists, dance feedback AI, singing feedback AI, painting critique AI, artist growth plan India"
      />

      <section className="px-5 pb-16 pt-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-8 lg:grid-cols-[1fr_0.85fr]">
            <div className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-xl sm:p-8">
              <div className="inline-flex items-center gap-2 rounded-full bg-purple-50 px-4 py-2 text-sm font-black uppercase tracking-[0.18em] text-purple-700">
                <Crown className="h-4 w-4" />
                Paid Artist Growth Mentor
              </div>
              <h1 className="mt-5 text-4xl font-black leading-tight text-slate-950 sm:text-5xl lg:text-6xl">
                KalaManch AI Mentor Pro
              </h1>
              <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-700">
                A premium AI mentor for serious artists. Upload or share your artwork, singing, dance, reel, or portfolio
                and get discipline-specific feedback, practice drills, market intelligence, pricing direction, and a growth roadmap.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <button type="button" onClick={() => document.getElementById('mentor-plans')?.scrollIntoView({ behavior: 'smooth' })} className="btn-primary">
                  View paid plans
                </button>
                <button type="button" onClick={() => document.getElementById('mentor-review')?.scrollIntoView({ behavior: 'smooth' })} className="btn-outline">
                  Try mentor review
                </button>
              </div>
            </div>

            <div className="rounded-[2rem] bg-gradient-to-br from-[#FF6F00] via-[#C2185B] to-[#4A148C] p-6 text-white shadow-xl">
              <p className="text-xs font-black uppercase tracking-[0.28em] text-orange-100">Paid access beta</p>
              <h2 className="mt-3 text-3xl font-black">Built for artists who want serious growth.</h2>
              <p className="mt-3 rounded-2xl bg-white/12 p-3 text-sm font-bold leading-6 text-orange-50">
                Payment gateway is not required yet. Artists request access, and KalaManch admin manually activates early paid plans during beta.
              </p>
              <div className="mt-5 space-y-3">
                {included.slice(0, 4).map((item) => (
                  <div key={item} className="flex gap-3 rounded-2xl bg-white/10 p-3 text-sm font-semibold leading-6">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-100" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {disciplines.map(({ key, icon: Icon, text }) => (
              <div key={key} className="rounded-[1.5rem] border border-orange-100 bg-white p-5 shadow-sm">
                <Icon className="h-8 w-8 text-[#FF6F00]" />
                <h3 className="mt-3 text-xl font-black text-slate-950">{key}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{text}</p>
              </div>
            ))}
          </div>

          <section id="mentor-plans" className="mt-12">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.28em] text-[#FF6F00]">Subscription plans</p>
                <h2 className="mt-2 text-3xl font-black text-slate-950">Choose the right AI Mentor plan</h2>
                <p className="mt-2 max-w-2xl text-sm font-semibold leading-6 text-slate-600">
                  During launch, these paid plans are activated manually by admin after an artist requests access.
                </p>
              </div>
              <div className="flex rounded-full border border-orange-200 bg-white p-1 shadow-sm">
                {['monthly', 'annual'].map((cycle) => (
                  <button
                    key={cycle}
                    type="button"
                    onClick={() => setBillingCycle(cycle)}
                    className={`rounded-full px-5 py-2 text-sm font-black capitalize ${billingCycle === cycle ? 'bg-[#FF6F00] text-white' : 'text-slate-700'}`}
                  >
                    {cycle}
                  </button>
                ))}
              </div>
            </div>

            {currentPlan && (
              <div className="mt-5 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm font-bold text-emerald-900">
                Current AI Mentor plan: {currentPlan.name}. Feedback remaining this month: {status?.feedback_remaining ?? 0}.
              </div>
            )}
            {status?.access_request?.status === 'pending' && (
              <div className="mt-5 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-sm font-bold text-amber-900">
                Your AI Mentor Pro request is pending admin review for the {plans.find((plan) => plan.id === status.access_request.plan_id)?.name || 'selected'} plan.
              </div>
            )}

            <div className="mt-6 grid gap-5 lg:grid-cols-3">
              {paidPlans.map((plan) => {
                const price = plan.id === 'payg' ? plan.monthly_price : (billingCycle === 'annual' ? plan.annual_price : plan.monthly_price);
                const active = selectedPlan === plan.id;
                return (
                  <article
                    key={plan.id}
                    className={`relative rounded-[2rem] border p-6 shadow-lg transition ${plan.highlight ? 'border-[#FF6F00] bg-gradient-to-br from-white to-orange-50 ring-4 ring-orange-100' : 'border-slate-200 bg-white'} ${active ? 'scale-[1.01]' : ''}`}
                  >
                    {plan.highlight && (
                      <div className="absolute right-5 top-5 rounded-full bg-[#4A148C] px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-white">
                        Best value
                      </div>
                    )}
                    <p className="text-xs font-black uppercase tracking-[0.22em] text-[#FF6F00]">{plan.badge}</p>
                    <h3 className="mt-3 text-2xl font-black text-slate-950">{plan.name}</h3>
                    <div className="mt-4 flex items-end gap-1 text-slate-950">
                      <IndianRupee className="mb-2 h-6 w-6" />
                      <span className="text-5xl font-black">{price}</span>
                      <span className="mb-2 text-sm font-bold text-slate-500">{plan.id === 'payg' ? '/review' : `/${billingCycle === 'annual' ? 'year' : 'month'}`}</span>
                    </div>
                    <ul className="mt-5 space-y-2">
                      {(plan.features || []).map((feature) => (
                        <li key={feature} className="flex gap-2 text-sm font-semibold leading-6 text-slate-700">
                          <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedPlan(plan.id);
                        requestAccess(plan.id);
                      }}
                      disabled={loading}
                      className={`mt-6 w-full rounded-full px-5 py-3 text-sm font-black ${plan.highlight ? 'bg-[#FF6F00] text-white shadow-lg shadow-orange-200' : 'border border-[#FF6F00] text-[#FF6F00] hover:bg-orange-50'}`}
                    >
                      {loading && active ? 'Sending request...' : plan.id === 'payg' ? 'Request feedback credit' : 'Request access'}
                    </button>
                  </article>
                );
              })}
            </div>
          </section>

          <section id="mentor-review" className="mt-12 grid gap-6 lg:grid-cols-[1fr_0.8fr]">
            <div className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg">
              <p className="text-xs font-black uppercase tracking-[0.28em] text-[#4A148C]">Premium review</p>
              <h2 className="mt-2 text-3xl font-black text-slate-950">Request AI Mentor feedback</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Upload an artwork image, add a public media link, or describe the work. AI Mentor will focus on the work itself unless you ask for promotion advice.
              </p>
              {!isAuthenticated && (
                <div className="mt-4 rounded-2xl border border-orange-200 bg-orange-50 p-4 font-bold text-orange-900">
                  Login as an artist to generate and save paid mentor feedback.
                </div>
              )}
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <select
                  value={feedbackForm.discipline}
                  onChange={(event) => {
                    const discipline = event.target.value;
                    const options = mentorSubOptions[discipline] || [];
                    setFeedbackForm((current) => ({ ...current, discipline, sub_discipline: options[0] || '' }));
                  }}
                  className="rounded-2xl border border-slate-200 px-4 py-3 font-semibold"
                >
                  {disciplines.map((item) => <option key={item.key}>{item.key}</option>)}
                </select>
                <select
                  value={feedbackForm.sub_discipline}
                  onChange={(event) => setFeedbackForm((current) => ({ ...current, sub_discipline: event.target.value }))}
                  className="rounded-2xl border border-slate-200 px-4 py-3 font-semibold"
                >
                  {(mentorSubOptions[feedbackForm.discipline] || ['General']).map((item) => <option key={item}>{item}</option>)}
                </select>
                <select
                  value={feedbackForm.media_type}
                  onChange={(event) => setFeedbackForm((current) => ({ ...current, media_type: event.target.value }))}
                  className="rounded-2xl border border-slate-200 px-4 py-3 font-semibold"
                >
                  <option value="image">Image / artwork</option>
                  <option value="audio">Audio / singing</option>
                  <option value="video">Video / dance</option>
                  <option value="link">Portfolio link</option>
                  <option value="text">Text description</option>
                </select>
                <input
                  value={feedbackForm.title}
                  onChange={(event) => setFeedbackForm((current) => ({ ...current, title: event.target.value }))}
                  placeholder="Title or performance name"
                  className="rounded-2xl border border-slate-200 px-4 py-3 font-semibold"
                />
                <input
                  value={feedbackForm.media_url}
                  onChange={(event) => setFeedbackForm((current) => ({ ...current, media_url: event.target.value }))}
                  placeholder="Media URL or public link"
                  className="rounded-2xl border border-slate-200 px-4 py-3 font-semibold"
                />
              </div>
              <label className="mt-4 flex cursor-pointer flex-col gap-2 rounded-2xl border border-dashed border-orange-200 bg-orange-50/60 px-4 py-4 text-sm font-bold text-slate-700">
                Upload image for AI Mentor review
                <span className="text-xs font-semibold text-slate-500">JPG, PNG, or WebP. Best for painting, rangoli, pottery, crochet, and craft feedback.</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="text-sm"
                  onChange={(event) => setMentorUpload(event.target.files?.[0] || null)}
                />
                {mentorUpload && <span className="text-xs font-black text-emerald-700">Selected: {mentorUpload.name}</span>}
              </label>
              <textarea
                value={feedbackForm.context}
                onChange={(event) => setFeedbackForm((current) => ({ ...current, context: event.target.value }))}
                placeholder="Tell AI Mentor what to review: goal, audience, problem area, city, pricing doubts, upcoming opportunity, or practice concern."
                className="mt-4 min-h-[150px] w-full rounded-2xl border border-slate-200 px-4 py-3 font-semibold leading-7"
              />
              <button type="button" onClick={submitFeedback} disabled={loading} className="btn-primary mt-4">
                <UploadCloud className="h-4 w-4" />
                {uploadingMentorMedia ? 'Uploading image...' : loading ? 'Generating...' : 'Generate mentor feedback'}
              </button>
            </div>

            <div className="rounded-[2rem] border border-purple-100 bg-white p-6 shadow-lg">
              <p className="text-xs font-black uppercase tracking-[0.28em] text-purple-700">Result</p>
              {notice && <div className="mt-3 rounded-2xl bg-emerald-50 p-4 text-sm font-bold text-emerald-800">{notice}</div>}
              {error && <div className="mt-3 rounded-2xl bg-red-50 p-4 text-sm font-bold text-red-700">{error}</div>}
              {review ? (
                <div className="mt-4 whitespace-pre-wrap rounded-2xl bg-slate-50 p-4 text-sm font-semibold leading-7 text-slate-800">{review}</div>
              ) : (
                <div className="mt-4 rounded-2xl bg-slate-50 p-5 text-sm leading-7 text-slate-600">
                  <Brain className="mb-3 h-8 w-8 text-[#FF6F00]" />
                  Your premium AI Mentor result will appear here with specific feedback, drills, market direction, and next actions.
                </div>
              )}
            </div>
          </section>

          <section className="mt-12 rounded-[2rem] bg-slate-950 p-6 text-white shadow-xl sm:p-8">
            <div className="grid gap-6 lg:grid-cols-[0.7fr_1fr]">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.28em] text-orange-200">Coming next</p>
                <h2 className="mt-2 text-3xl font-black">This becomes the paid AI product for Kalamanch.</h2>
                <p className="mt-3 leading-7 text-slate-300">
                  Android and iOS can use the same AI Mentor APIs. Admin can manually activate plans now, then Razorpay checkout can be connected when subscriptions go live.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {included.map((item) => (
                  <div key={item} className="rounded-2xl bg-white/10 p-4 text-sm font-semibold leading-6">
                    <LineChart className="mb-2 h-5 w-5 text-orange-200" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </section>
    </main>
  );
};

export default AiMentor;
